import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { AnserMark } from "@/components/ui/anser-mark";
import { isRailSuppressed } from "@/components/coach/rail-visibility";
import { useCoachRail } from "@/context/CoachRailContext";
import { useIntelligenceAccess } from "@/hooks/use-intelligence-access";
import { cn } from "@/lib/utils";

/**
 * The header's "Anser" button from the sketch: a lilac pill with the mark
 * that shows or hides the Anser panel. The open state is persisted per
 * business by `CoachRailContext`. Hidden where the panel itself is (the
 * full-page chat) and for accounts without Intelligence.
 */
export function AnserToggle() {
  const { t } = useTranslation("nav");
  const { pathname } = useLocation();
  const entitled = useIntelligenceAccess();
  const { open, toggleRail } = useCoachRail();

  if (!entitled || isRailSuppressed(pathname)) return null;

  return (
    <button
      type="button"
      onClick={toggleRail}
      aria-pressed={open}
      aria-label={open ? t("anser.hide") : t("anser.show")}
      title={open ? t("anser.hide") : t("anser.show")}
      className={cn(
        "inline-flex h-10 items-center gap-2 rounded-g-md px-3.5 text-[13.5px] font-semibold transition-colors duration-150 ease-out",
        "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-g-ring",
        open
          ? "bg-g-primary-tint text-g-primary-press hover:bg-g-primary-wash"
          : "border border-g-line bg-g-bg-card text-g-ink-700 hover:bg-g-primary-wash hover:text-g-ink-900",
      )}
    >
      <AnserMark size={18} />
      {t("anser.toggle")}
    </button>
  );
}
